// The setup screen's dialogs, mounted once: the value picker, a grid cell's
// sets, Preview, Publish, History, Import / export and the code dialog.

import type { ComponentType } from 'react'
import { KitModal } from '../../designerKit/KitModal'
import { addCell, removeCell, setCovers } from '../rules'
import { formOf } from './model'
import { opSet, useSetup } from './state'
import ValuePicker from './ValuePicker'
import PreviewDialog from './PreviewDialog'
import PublishDialog from './PublishDialog'
import HistoryDialog from './HistoryDialog'
import ImportExportDialog from './ImportExportDialog'
import CodeDialog from './CodeDialog'
import { SetDot } from './ui'

function CellDialog({ combo, onClose }: { combo: Record<string, string>; onClose: () => void }) {
  const { formNo, draft, labelOf, mode, notify } = useSetup()
  const form = formOf(draft.layer, formNo)
  const lab = labelOf(formNo)
  const drivers = form?.drivers || []
  const title = drivers.map(d => lab(d.key, combo[d.key])).join(' · ')
  const sets = form?.sets || []
  const asked = sets.filter(s => setCovers(s, combo, drivers))
  return (
    <KitModal open onClose={onClose} title={title || 'Combination'}
      footer={<button type="button" className="btn btn-primary btn-sm" onClick={onClose}>Done</button>}>
      <div className="uds-dlg" style={{ display: 'grid', gap: 6 }}>
        <div className="uds-hint" style={{ margin: '0 0 4px' }}>Attribute sets asked for this combination</div>
        {sets.map(s => {
          const on = setCovers(s, combo, drivers)
          const locked = mode === 'tenant' && s.origin === 'design' && !(s.tenant_may || []).includes('rules')
          return (
            <label key={s.key} className="uds-li" style={{ cursor: locked ? 'not-allowed' : 'pointer' }}>
              <input type="checkbox" checked={on} disabled={locked}
                onChange={() => {
                  if (locked) { notify('info', 'Who gets this standard set is fixed by the application.'); return }
                  draft.edit(opSet(formNo, s.key, (set, f) => {
                    set.rules = on ? removeCell(set.rules || [], combo, f.drivers) : addCell(set.rules || [], combo, f.drivers)
                  }))
                }} />
              <SetDot color={s.color} /><span className="t">{s.label || s.key}</span>
              <span style={{ color: 'var(--color-text-faint)' }}>{(s.columns || []).length} columns</span>
            </label>
          )
        })}
        <div className="uds-hint">{asked.reduce((n, s) => n + (s.columns || []).length, 0)} columns in total.</div>
      </div>
    </KitModal>
  )
}

export default function SetupDialogs({ codeEditor }: {
  codeEditor?: ComponentType<{ value: string; onChange: (v: string) => void; language: string }>
}) {
  const { dialog, openDialog } = useSetup()
  const close = () => openDialog(null)
  if (!dialog) return null
  switch (dialog.kind) {
    case 'picker': return <ValuePicker setKey={dialog.set} ruleIndex={dialog.rule} driverKey={dialog.driver} onClose={close} />
    case 'cell': return <CellDialog combo={dialog.combo} onClose={close} />
    case 'preview': return <PreviewDialog onClose={close} />
    case 'publish': return <PublishDialog onClose={close} />
    case 'history': return <HistoryDialog onClose={close} />
    case 'import': return <ImportExportDialog onClose={close} />
    case 'code': return <CodeDialog fnName={dialog.fnName} title={dialog.title} onClose={close} codeEditor={codeEditor} />
    default: return null
  }
}
